import clsx from 'clsx';
import { ReactComponent as Add } from '../../../../../../Asset/svg/add_white.svg';
import { ReactComponent as RemoveB } from '../../../../../../Asset/svg/removeB.svg';
import { useEditSharedStyles } from '../Edit.styles';
import { useQcmEditorStyles } from './QcmBlockEditor.styles';
import { EbookContentBlock, getQuestionType, QUESTION_TYPE_OPTIONS, QuestionType } from '../types';

type QcmBlockEditorProps = {
  block: EbookContentBlock;
  blockIndex: number;
  onQuestionChange: (value: string) => void;
  onQuestionTypeChange: (type: QuestionType) => void;
  onOptionChange: (responseIndex: number, value: string) => void;
  onAddOption: (isOther?: boolean) => void;
  onDeleteOption: (responseIndex: number) => void;
};

const QcmBlockEditor = ({
  block,
  blockIndex,
  onQuestionChange,
  onQuestionTypeChange,
  onOptionChange,
  onAddOption,
  onDeleteOption,
}: QcmBlockEditorProps) => {
  const classes = useQcmEditorStyles();
  const shared = useEditSharedStyles();
  const questionType = getQuestionType(block);
  const responses = block?.qcm?.[0]?.response || [];
  const hasOtherOption = responses.some(response => response.isOther);

  return (
    <div className={classes.root}>
      <textarea
        className={classes.questionInput}
        placeholder="Entrez la question"
        value={block.qcm_title || ''}
        onChange={event => onQuestionChange(event.target.value)}
        rows={2}
        aria-label="Question"
      />

      <label className={classes.fieldGroup} htmlFor={`question-type-${blockIndex}`}>
        <span className={classes.fieldLabel}>Type de question</span>
        <select
          id={`question-type-${blockIndex}`}
          className={classes.select}
          value={questionType}
          onChange={event => onQuestionTypeChange(event.target.value as QuestionType)}>
          {QUESTION_TYPE_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <div className={classes.optionsList}>
        {responses.map((response, responseIndex) => (
          <div
            key={response.id ?? response.idx ?? `option-${blockIndex}-${responseIndex}`}
            className={classes.optionRow}>
            {response.isOther ? (
              <div className={classes.otherBlock}>
                <div className={classes.otherLabel}>
                  <span
                    className={clsx(classes.choiceIndicator, {
                      [classes.choiceIndicatorCheckbox]: questionType === 'checkbox',
                    })}
                    aria-hidden="true"
                  />
                  Autre
                </div>
                <textarea
                  className={classes.otherPreview}
                  value={response.responseText || ''}
                  placeholder="Champ texte réservé aux bénéficiaires"
                  readOnly={true}
                  rows={2}
                />
              </div>
            ) : (
              <>
                <span
                  className={clsx(classes.choiceIndicator, {
                    [classes.choiceIndicatorCheckbox]: questionType === 'checkbox',
                  })}
                  aria-hidden="true"
                />
                <textarea
                  className={classes.optionInput}
                  placeholder="Entrer une réponse"
                  value={response.responseText || ''}
                  onChange={event => onOptionChange(responseIndex, event.target.value)}
                  rows={1}
                  aria-label={`Option ${responseIndex + 1}`}
                />
              </>
            )}
            <button
              type="button"
              className={classes.deleteOptionBtn}
              aria-label="Supprimer l'option"
              onClick={() => onDeleteOption(responseIndex)}>
              <RemoveB className={classes.deleteOptionIcon} />
            </button>
          </div>
        ))}

        {!hasOtherOption && (
          <button type="button" className={classes.addOtherLink} onClick={() => onAddOption(true)}>
            <span aria-hidden="true">+</span>
            Ajouter &quot;Autre&quot;
          </button>
        )}
      </div>

      <button type="button" className={classes.addOptionButton} onClick={() => onAddOption(false)}>
        <Add className={shared.whiteActionIcon} />
        Ajouter une option
      </button>
    </div>
  );
};

export default QcmBlockEditor;
